import React from "react";
import ReactDOM from "react-dom";
import {
	commitLocalUpdate,
	fetchQuery,
	graphql,
	useFragment,
	useLazyLoadQuery,
	useRefetchableFragment,
} from "react-relay";
import { Link, type RouteComponentProps, useLocation } from "react-router-dom";
import type { SingleValue } from "react-select";

import { faCheck, faLink } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faEllipsisV } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faSparklingSpeechBubble } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { noop } from "lodash";

import {
	ActionItemsRecordingContext,
	type ActionItemsRecordingContextType,
} from "~/actionItems/newActionItems/ActionItemsContext";
import { AppContext } from "~/common";
import { CLIPS_TAB, HIGHLIGHTS_TAB, STATS_TAB, TRANSCRIPT_TAB } from "~/constants/meetingRecorderTabs";
import { ReactComponent as FellowIcon } from "~/legacy-assets/images/logos/fellow-icon-white.svg";
import { commitMutationPromise } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import store from "~/lib/store";
import { withSuspense } from "~/lib/suspense";
import { getFirstUserFacingError } from "~/lib/utils";
import { useGroupEmitter } from "~/lib/websocket";
import { ASK_COPILOT_NEW_USER_KEY, useTrackAskCopilotOpened } from "~/meetingRecorder/AskCopilotPanel";
import { ClipCutModal } from "~/meetingRecorder/ClipCutModal";
import { type CopyRecapOptions, copyRecapSection, type RecapSection } from "~/meetingRecorder/copyUtils";
import TranscriptPreviewFailedModal from "~/meetings/TranscriptPreviewFailedModal";
import { usePaywallCheck } from "~/meetings/utils";
import MiniPlayer from "~/miniPlayer";
import { environment } from "~/models";
import AskFellowPanel from "~/nav/panels/AskFellow";
import type { DeletionType } from "~/stream/DeleteNoteModal/DeleteNoteModal";
import { AnchorButton, Button } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";
import { IconButton, Modal, ToggleGroup } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import Menu from "~/ui/menu";
import { __globalModalContext, type ModalProps, useModals } from "~/ui/modals";
import Selector from "~/ui/selectors/Selector";
import type { SelectorOption } from "~/ui/selectors/SelectorUtils";
import toast from "~/ui/toast";

import AccountStore from "../account/AccountStore";
import { showFeatureModal } from "../billing";
import MeetingContainerTabs, { type CurrentTabType } from "../stream/MeetingContainer/MeetingContainerTabs";
import { ChaptersPanel, type ChaptersPanelRef } from "./ChaptersPanel";
import { closeClipModal, openClipModal } from "./ClipModal";
import { ClipsPanel } from "./ClipsPanel";
import {
	RECORDING_SHOW_VIDEO,
	RECORDING_VIDEO_PLAYER_READY,
	RECORDING_VIDEO_START_PLAYING,
	type RecordingVideoPlayerReadyEvent,
	type RecordingVideoStartPlayingEvent,
} from "./constants";
import { ReactComponent as AskCopilotChevronUp } from "./css/ask-copilot-chevron-up.svg";
import DislikeModal from "./DislikeModal";
import { clearLikedSectionCacheForRecording, Highlights } from "./HighlightsPanel";
import { MeetingContainerModalNote } from "./MeetingContainerModalNote";
import { StatsPanel } from "./StatsPanel";
import { SummaryText } from "./SummaryText";
import { RecordingContext, TranscriptModalContext, TranscriptModalContextProvider } from "./TranscriptModalContext";
import TranscriptModalRegenerating, { DefaultVideoPreview, RedactingPreview } from "./TranscriptModalGenerating";
import { TranscriptModalHeader } from "./TranscriptModalHeader";
import {
	confirmDiscardRedactions,
	type SpeakerTranscriptSegment,
	Transcript,
	type TranscriptRef,
} from "./TranscriptPanel";
import TranscriptVideo, { CollapseButton } from "./TranscriptVideo";
import { useOnTimestampClick } from "./useOnTimestampClick";
import {
	copyToClipboard,
	formatDuration,
	getTimestampFromLocation,
	HighlightFeedbackSource,
	PublicContextProvider,
	useAddedOrAvailableUntil,
	useAutoRefetchMeetingRecordings,
	useGetURL,
	useIsNarrowViewport,
	useIsPublic,
	useLongestMeetingRecording,
	usePublicAccessKey,
} from "./utils";
import { processRecordingCommentUpdates } from "./utils";
import { useSessionManager } from "./Video/SessionManager";

import type { HighlightsPanel_note$key } from "~/__generated__/HighlightsPanel_note.graphql";
import type { HighlightsPanel_viewer$key } from "~/__generated__/HighlightsPanel_viewer.graphql";
import type { StatsPanel_note$key } from "~/__generated__/StatsPanel_note.graphql";
import type { StatsPanel_viewer$key } from "~/__generated__/StatsPanel_viewer.graphql";
import type { TranscriptModal_meetingRecording$key } from "~/__generated__/TranscriptModal_meetingRecording.graphql";
import type {
	TranscriptModal_note$key,
	TranscriptModal_note$data as TranscriptModalNote,
} from "~/__generated__/TranscriptModal_note.graphql";
import type { TranscriptModal_PublicClipModal_clip$key } from "~/__generated__/TranscriptModal_PublicClipModal_clip.graphql";
import type { TranscriptModal_PublicClipModal_videoPlaybackSettings$key } from "~/__generated__/TranscriptModal_PublicClipModal_videoPlaybackSettings.graphql";
import type { TranscriptModal_RecordingsRenderer_note$key } from "~/__generated__/TranscriptModal_RecordingsRenderer_note.graphql";
import type { TranscriptModal_viewer$key } from "~/__generated__/TranscriptModal_viewer.graphql";
import type { TranscriptModalClipsRefetchQuery } from "~/__generated__/TranscriptModalClipsRefetchQuery.graphql";
import type { TranscriptModalContent_note$key } from "~/__generated__/TranscriptModalContent_note.graphql";
import type { TranscriptModalContentStats$key } from "~/__generated__/TranscriptModalContentStats.graphql";
import type { TranscriptModalContentStatsRefetchQuery } from "~/__generated__/TranscriptModalContentStatsRefetchQuery.graphql";
import type {
	RecapTemplateType,
	TranscriptModalGenerateRecapTemplateMutation,
} from "~/__generated__/TranscriptModalGenerateRecapTemplateMutation.graphql";
import type { TranscriptModalHighlightsRefetchQuery } from "~/__generated__/TranscriptModalHighlightsRefetchQuery.graphql";
import type { TranscriptModalPublicClipQuery } from "~/__generated__/TranscriptModalPublicClipQuery.graphql";
import type { TranscriptModalQuery } from "~/__generated__/TranscriptModalQuery.graphql";
import type { TranscriptModalRecordingIdQuery } from "~/__generated__/TranscriptModalRecordingIdQuery.graphql";
import type { TranscriptModalRegenerateSummaryMutation } from "~/__generated__/TranscriptModalRegenerateSummaryMutation.graphql";
import type { TranscriptPanel_Transcript_viewer$key } from "~/__generated__/TranscriptPanel_Transcript_viewer.graphql";
import type { TranscriptVideo_videoPlaybackSettings$key } from "~/__generated__/TranscriptVideo_videoPlaybackSettings.graphql";

import styles from "./css/TranscriptModal.module.css";

export interface TranscriptChapter {
	readonly timestamp?: number | null;
	readonly duration?: number | null;
	readonly title: string;
}

const SHOWED_SURVEY_KEY = "transcript-modal-survey";
// 5 minutes
const MIN_TIME_FOR_SURVEY = 1000 * 60 * 5;
// 30 seconds
const DELAY_TO_SHOW_THE_SURVEY = 1000 * 30;
const INTERCOM_SURVEY_ID = 36637829;

const regenerateFeedbackMutation = graphql`
	mutation TranscriptModalRegenerateSummaryMutation($meetingRecordingId: ID!) {
		regenerateSummary(meetingRecordingId: $meetingRecordingId) {
			ok
		}
	}
`;

const generateRecapTemplateMutation = graphql`
	mutation TranscriptModalGenerateRecapTemplateMutation($meetingRecordingId: ID!, $templateType: RecapTemplateType!) {
		generateRecapTemplate(meetingRecordingId: $meetingRecordingId, templateType: $templateType) {
			ok
			meetingRecording {
				isGeneratingRecapTemplate
			}
		}
	}
`;

const highlightsRefetchQuery = graphql`
	query TranscriptModalHighlightsRefetchQuery($recordingId: ID!) {
		meetingRecording: node(id: $recordingId) {
			...HighlightsPanel_meetingRecording @arguments(public: false)
			...ChaptersPanel_meetingRecording
		}
	}
`;

export function timestampToHoursMinutesSeconds(timestamp: number) {
	const seconds = Math.floor(timestamp % 60),
		minutes = Math.floor((timestamp % 3600) - seconds) / 60,
		hours = Math.floor(timestamp / 3600);
	return { hours, minutes, seconds };
}

export interface TranscriptModalContentRef {
	transcriptRef: React.RefObject<TranscriptRef>;
	chaptersRef: React.RefObject<ChaptersPanelRef>;
	openClipCutModal: (initialTimestamp?: number, initialEndTimestamp?: number) => void;
}

export type TranscriptModalContentTab = "Highlights" | "Transcript" | "Clips" | "Stats" | "Chapters";
export type TranscriptMode = "default" | "redacting";

const isTranscriptModalContentTab = (tab: string): tab is TranscriptModalContentTab => {
	return ["Highlights", "Transcript", "Clips", "Stats", "Chapters"].includes(tab);
};

interface TranscriptModalContentProps {
	note: TranscriptModalContent_note$key | null;
	highlightsNoteKey: HighlightsPanel_note$key | null;
	statsNoteKey: StatsPanel_note$key | null;
	viewer: HighlightsPanel_viewer$key & TranscriptPanel_Transcript_viewer$key & StatsPanel_viewer$key;
	videoRef: React.RefObject<HTMLVideoElement>;
	chaptersRef: React.RefObject<ChaptersPanelRef>;
	onTimestampClick: (timestamp: number, recordingId?: string) => void;
	meetingRecording: TranscriptModal_meetingRecording$key;
	videoPlaybackSettings: TranscriptVideo_videoPlaybackSettings$key;
	duration: number | null;
	thumbnailTrack: TextTrack | null;
	initialTab?: TranscriptModalContentTab;
	onClipSelect: (val: string) => void;
	closeModal?: () => void;
	mode: TranscriptMode;
	setMode: React.Dispatch<React.SetStateAction<TranscriptMode>>;
	fromNote?: boolean; // The component is inserted in the note, not in the modal
	forceIsNarrow?: boolean;
	parts?: { value: string; label: string }[];
	setSelectedRecordingId?: (recordingId: string) => void;
	allowRecapDownload?: boolean;
}

export const TranscriptModalContent = React.forwardRef<
	TranscriptModalContentRef,
	React.PropsWithChildren<TranscriptModalContentProps>
>(function TranscriptModalContent(
	{
		note: noteKey,
		highlightsNoteKey,
		statsNoteKey,
		viewer,
		videoRef,
		chaptersRef,
		onTimestampClick,
		meetingRecording: meetingRecordingKey,
		videoPlaybackSettings,
		duration,
		children,
		thumbnailTrack,
		initialTab,
		onClipSelect,
		closeModal,
		mode,
		setMode,
		fromNote,
		forceIsNarrow,
		parts,
		setSelectedRecordingId,
		allowRecapDownload,
	},
	ref,
) {
	const [aiDetectedActionItemsListRefetchKey, setAiDetectedActionItemsListRefetchKey] = React.useState(0);

	const { analyticsFingerprint } = React.useContext(TranscriptModalContext);
	const isPublic = useIsPublic();
	const isLoggedInSomewhere = AccountStore.getAll().length > 0 || !isPublic;

	const { user } = React.useContext(AppContext);

	const note = useFragment(
		graphql`
			fragment TranscriptModalContent_note on Note {
				id
				__typename
				viewerCanEdit
				...TranscriptPanel_note
			}
		`,
		noteKey,
	);
	const [meetingRecording, refetch] = useRefetchableFragment(
		graphql`
			fragment TranscriptModal_meetingRecording on MeetingRecording
			@argumentDefinitions(public: { type: "Boolean!" })
			@refetchable(queryName: "TranscriptModalContentQuery") {
				__typename
				id
				title
				url @skip(if: $public)
				subtitlesVttUrl @skip(if: $public)
				meetingRecordedVideoDuration
				meetingRecordedVideoUrl
				transcript
				activeSummary {
					id
					summary
					chapters {
						timestamp
						title
						bulletPoints {
							text
							timestamp
							textShort
							context
						}
					}
					actionItems {
						timestamp
						text
						assignees {
							userId
							fullName
						}
					}
					decisions {
						timestamp
						text
					}
					activeRecapTemplate @skip(if: $public)
					templatedRecaps @skip(if: $public)
				}
				clips @skip(if: $public) {
					id
					clipStart
					clipEnd
					...ClipsPanel_ClipSummary_clip
				}
				isGeneratingRecapTemplate
				redactionIsInProgress
				regenerationForLanguageIsInProgress
				hasAskCopilotConversation
				thumbnailVttUrl
				meetingCallStart
				recordingAndTranscriptWillBeDeleted
				transcriptDeletionReason
				meetingRecordingVideoDeletionReason
				recordingDataAutoDeletionDate
				userVideoProgress
				ownerAccount {
					__typename
					id
				}
				note {
					title
				}
				...Timeline_meetingKeyEvents @skip(if: $public)
				...HighlightsPanel_meetingRecording @arguments(public: $public)
				...ChaptersPanel_meetingRecording
				...TranscriptVideo_meetingRecording @arguments(public: $public)
				...ClipsPanel_meetingRecording @skip(if: $public)
				...TranscriptPanel_Transcript_meetingRecording @arguments(public: $public)
				...ClipCutModal_meetingRecording @skip(if: $public)
				...DislikeModal_meetingRecording
				...TranscriptModalContentStats
				...TranscriptModalGenerating_RedactingPreview_meetingRecording
			}
		`,
		meetingRecordingKey,
	);

	const [statsMeetingRecording, statsRefetch] = useRefetchableFragment<
		TranscriptModalContentStatsRefetchQuery,
		TranscriptModalContentStats$key
	>(
		graphql`
			fragment TranscriptModalContentStats on MeetingRecording
			@refetchable(queryName: "TranscriptModalContentStatsRefetchQuery") {
				meetingCallStart
				meetingCallEnd
				meetingRecordedVideoDuration
				meetingStats {
					userStats {
						email
						fullName
						image
						isGuest
						isTopUser
						absent
						totalTalkTime
						percentage
						joinedAt
						leftAt
					}
					actualStartTime
					actualEndTime
				}
			}
		`,
		meetingRecording,
	);

	const errorRegeneration = React.useCallback(() => {
		setRegenerating(false);
		toast.error("Failed to regenerate recap");
	}, []);

	const successRegeneration = React.useCallback(() => {
		refetch({ id: meetingRecording.id });
		setAiDetectedActionItemsListRefetchKey(c => c + 1);
		setRegenerating(false);
		toast.success("Recap successfully regenerated");
	}, [refetch, meetingRecording.id]);

	type SocketPayload =
		| { type: "SUMMARY_SECTION_EDIT_RECEIVED"; data: { updated_section: string } }
		| {
				type: "COMMENTS_CHANGED";
				data: {
					threadId: string;
					startTimestamp: string;
					endTimestamp: string;
					count: number;
					users: Array<{ id: string; email: string; fullName: string; image: string }>;
					targetUsers: Array<{ id: string; fullName: string }>;
					commentOperation: string;
				};
		  }
		| { type: "MEETING_RECORDING_REGENERATION_FAILED"; data: { user_id_who_regenerated: string } }
		| { type: "MEETING_RECORDING_CLIP_GENERATED"; data: Record<string, unknown> }
		| { type: "MEETING_RECORDING_REGENERATION_COMPLETE"; data: Record<string, unknown> }
		| { type: "MEETING_RECORDING_TEMPLATE_GENERATION_COMPLETE"; data: Record<string, unknown> }
		| { type: "MEETING_RECORDING_TEMPLATE_GENERATION_FAILED"; data: { user_id_who_regenerated: string } }
		| { type: "MEETING_RECORDING_STATS_GENERATION_COMPLETE"; data: Record<string, unknown> };

	const onSocketMessage = React.useCallback(
		async (payload: SocketPayload) => {
			if (payload.type === "SUMMARY_SECTION_EDIT_RECEIVED" && !isPublic) {
				fetchQuery<TranscriptModalHighlightsRefetchQuery>(environment, highlightsRefetchQuery, {
					recordingId: meetingRecording.id,
				}).toPromise(); // This needs to be promise-d in order to trigger the re-render
				setAiDetectedActionItemsListRefetchKey(c => c + 1);
			} else if (payload.type === "COMMENTS_CHANGED") {
				processRecordingCommentUpdates(environment, payload, meetingRecording.id, user?.id);
			} else if (payload.type === "MEETING_RECORDING_CLIP_GENERATED" && !isPublic) {
				fetchQuery<TranscriptModalClipsRefetchQuery>(
					environment,
					graphql`
						query TranscriptModalClipsRefetchQuery($recordingId: ID!) {
							meetingRecording: node(id: $recordingId) {
								__typename
								... on MeetingRecording {
									clips {
										id
										clipStart
										clipEnd
										...ClipsPanel_ClipSummary_clip
									}
									...ClipsPanel_meetingRecording
								}
							}
						}
					`,
					{
						recordingId: meetingRecording.id,
					},
				).toPromise(); // This needs to be promise-d in order to trigger the re-render
			} else if (payload.type === "MEETING_RECORDING_REGENERATION_COMPLETE") {
				successRegeneration();
			} else if (
				payload.type === "MEETING_RECORDING_REGENERATION_FAILED" &&
				// We only want to show the error message for the user that started the regeneration
				payload.data.user_id_who_regenerated === user?.id
			) {
				errorRegeneration();
			} else if (payload.type === "MEETING_RECORDING_TEMPLATE_GENERATION_COMPLETE") {
				fetchQuery<TranscriptModalHighlightsRefetchQuery>(environment, highlightsRefetchQuery, {
					recordingId: meetingRecording.id,
				})
					.toPromise()
					.then(() => {
						setGeneratingRecapTemplate(false);
						setRecapTemplateType(undefined);
						setAiDetectedActionItemsListRefetchKey(c => c + 1);
						toast.success("Recap template successfully generated");
					}); // This needs to be promise-d in order to trigger the re-render
			} else if (payload.type === "MEETING_RECORDING_TEMPLATE_GENERATION_FAILED") {
				setGeneratingRecapTemplate(false);
				setRecapTemplateType(undefined);
				// We only want to show the error message for the user that tried to generate the template
				if (payload.data.user_id_who_regenerated === user?.id) {
					toast.error("Failed to generate recap template");
				}
			} else if (payload.type === "MEETING_RECORDING_STATS_GENERATION_COMPLETE") {
				statsRefetch({}, { fetchPolicy: "network-only" });
				setAiDetectedActionItemsListRefetchKey(c => c + 1);
			}
		},
		[isPublic, user?.id, meetingRecording.id, successRegeneration, errorRegeneration, statsRefetch],
	);

	const socket = useGroupEmitter(`meeting_recordings.${meetingRecording.id.split("=").join("")}`);

	React.useEffect(() => {
		socket.on("message", onSocketMessage);
		return () => {
			socket.off("message", onSocketMessage);
		};
	}, [onSocketMessage, socket]);

	const onTranscriptSegmentClick = React.useCallback(
		(transcriptSegment: SpeakerTranscriptSegment) => {
			const video = videoRef.current;
			if (!video) return;

			// Subtract half a second to make sure you get the first word; the browser
			// might round this number.
			video.currentTime = Math.max(0, transcriptSegment.words[0].start_timestamp - 0.5);
			video.play();

			dispatchAnalyticsTrack("Meeting recordings transcript segment click", {
				fingerprint: analyticsFingerprint,
				note_id: note?.id, // eslint-disable-line camelcase
			});
		},
		[note, videoRef, analyticsFingerprint],
	);

	const pauseVideo = React.useCallback(() => {
		videoRef.current?.pause();
	}, [videoRef]);

	const doCloseModal = React.useCallback(() => closeModal?.(), [closeModal]);
	const modals = useModals();
	const openClipCutModal = React.useCallback(
		async (initialTimestamp?: number, initialEndTimestamp?: number) => {
			if (isPublic) {
				// ClipCutModal is not available in public contexts
				return;
			}

			const cutModalResult = await modals.open<boolean>(({ close }) => (
				<ClipCutModal
					meetingRecordingKey={meetingRecording}
					note={note}
					videoPlaybackSettings={videoPlaybackSettings}
					allowRecapDownload={allowRecapDownload}
					close={close}
					initialTimestamp={initialTimestamp}
					initialEndTimestamp={initialEndTimestamp}
					fromNote={fromNote}
				/>
			));

			if (cutModalResult) {
				setActiveTab("Clips");
			}
		},
		[modals, meetingRecording, note, videoPlaybackSettings, fromNote, isPublic, allowRecapDownload],
	);

	const transcriptRef = React.useRef<TranscriptRef>(null);
	React.useImperativeHandle(ref, () => ({
		transcriptRef,
		chaptersRef,
		openClipCutModal,
	}));

	const appContext = React.useContext(AppContext);
	const isUserAdmin = appContext.user?.internalAttributes?.isAccountAdmin ?? false;
	const narrowViewport = useIsNarrowViewport() || forceIsNarrow;

	const onPartChange = React.useCallback(
		(val: SingleValue<SelectorOption>) => {
			if (!val || !setSelectedRecordingId) return;

			setSelectedRecordingId(val.value);
		},
		[setSelectedRecordingId],
	);

	const recordingUnavailableLabel =
		meetingRecording.meetingRecordingVideoDeletionReason === "AUTO" ? (
			<div className={styles.recordingUnavailableParagraph}>
				This video was automatically deleted based on{" "}
				{isPublic ? (
					<>the workspace settings</>
				) : isUserAdmin ? (
					<>
						your{" "}
						<Link to="/account/copilot/settings/" onClick={closeModal}>
							workspace settings
						</Link>
					</>
				) : (
					<>your workspace settings</>
				)}
				.
			</div>
		) : (
			<div className={styles.recordingUnavailableParagraph}>This video was deleted.</div>
		);

	const recordingUnavailable = (
		<div className={styles.recordingUnavailableContainer}>
			{parts && parts.length > 1 ? (
				<div className={styles.recordingUnavailableFlexRow}>
					{recordingUnavailableLabel}
					<Selector
						condensed
						options={parts}
						value={parts.find(part => part.value === meetingRecording.id)}
						onChange={onPartChange}
					/>
				</div>
			) : (
				recordingUnavailableLabel
			)}
		</div>
	);

	const [isRegenerating, setRegenerating] = React.useState(false);
	const onRegenerate = React.useCallback(() => {
		setRegenerating(true);
		commitMutationPromise<TranscriptModalRegenerateSummaryMutation>(environment, {
			mutation: regenerateFeedbackMutation,
			variables: {
				meetingRecordingId: meetingRecording.id,
			},
			onError: () => {
				errorRegeneration();
			},
		});
		clearLikedSectionCacheForRecording(meetingRecording.id);
	}, [meetingRecording, errorRegeneration]);

	const [isGeneratingRecapTemplate, setGeneratingRecapTemplate] = React.useState(
		meetingRecording.isGeneratingRecapTemplate ?? false,
	);

	const [recapTemplateType, setRecapTemplateType] = React.useState<RecapTemplateType | undefined>(undefined);
	const onGenerateTemplate = React.useCallback(
		(templateType: RecapTemplateType) => {
			commitMutationPromise<TranscriptModalGenerateRecapTemplateMutation>(environment, {
				mutation: generateRecapTemplateMutation,
				variables: {
					meetingRecordingId: meetingRecording.id,
					templateType: templateType,
				},
				onCompleted: (response, errors) => {
					if (errors || !response.generateRecapTemplate?.ok) {
						const message = getFirstUserFacingError(errors) ?? "Something went wrong, please try again later";
						toast.error(message);
					} else {
						setGeneratingRecapTemplate(true);
						setRecapTemplateType(templateType);
					}
				},
				onError: () => {
					setGeneratingRecapTemplate(false);
					setRecapTemplateType(undefined);
					toast.error("Failed to generate recap template");
				},
			});
		},
		[meetingRecording.id],
	);

	const onDislike = React.useCallback(
		async (source: HighlightFeedbackSource) => {
			const feedbackResult: { feedback: string; otherFeedback: string } | null = await modals.open(({ close }) => (
				<DislikeModal meetingRecording={meetingRecording} source={source} onRegenerate={onRegenerate} close={close} />
			));
			dispatchAnalyticsTrack("[AI Copilot] Dislike Clicked", {
				fingerprint: analyticsFingerprint,
				section: source,
				recordingId: meetingRecording.id,
				recordingTitle: meetingRecording.title,
				feedback: feedbackResult,
			});
		},
		[modals, analyticsFingerprint, meetingRecording, onRegenerate],
	);

	const copyRecordingSection = React.useCallback(
		async (section: RecapSection, options?: CopyRecapOptions) => {
			await copyRecapSection(meetingRecording, section, options);
		},
		[meetingRecording],
	);

	const askCopilotEnabled =
		!!window.INITIAL_STATE.user &&
		(appContext.account?.allowAiFeatures || meetingRecording.hasAskCopilotConversation) &&
		!appContext.user?.isGuest;
	const [askCopilotOpen, setAskCopilotOpen] = React.useState(false);

	const [hasTrackedLowLanguageConfidenceScoreBannerShown, setHasTrackedLowLanguageConfidenceScoreBannerShown] =
		React.useState(false);

	const trackLowLanguageConfidenceScoreBannerShown = React.useCallback(
		analyticsData => {
			if (hasTrackedLowLanguageConfidenceScoreBannerShown) return;
			dispatchAnalyticsTrack("[AI Copilot] Low language confidence score banner displayed", analyticsData);
			setHasTrackedLowLanguageConfidenceScoreBannerShown(true);
		},
		[hasTrackedLowLanguageConfidenceScoreBannerShown],
	);

	const location = useLocation();
	const initialTabFromURL: TranscriptModalContentTab | undefined = React.useMemo(() => {
		const tab = location.search ? new URLSearchParams(location.search).get("tab") : null;
		if (tab && isTranscriptModalContentTab(tab)) {
			return tab;
		}
		return undefined;
	}, [location.search]);

	const [activeTab, setActiveTab] = React.useState<TranscriptModalContentTab>(
		initialTabFromURL ?? initialTab ?? "Highlights",
	);
	const [mobileActiveTab, setMobileActiveTab] = React.useState<TranscriptModalContentTab>(
		initialTabFromURL ?? initialTab ?? "Highlights",
	);

	const onValueChange = React.useCallback(
		(value: TranscriptModalContentTab) => {
			setActiveTab(value);
		},
		[setActiveTab],
	);

	const onMobileValueChange = React.useCallback(
		(value: TranscriptModalContentTab) => {
			setMobileActiveTab(value);
		},
		[setMobileActiveTab],
	);

	const recordingContextValues = React.useMemo<ActionItemsRecordingContextType>(() => {
		return {
			noteId: note?.id ?? "",
			recordingId: meetingRecording.id,
			videoDuration: duration ?? 0,
			onTimestampClick,
			aiDetectedActionItemsListRefetchKey,
			setAiDetectedActionItemsListRefetchKey,
		};
	}, [
		note?.id,
		meetingRecording.id,
		duration,
		onTimestampClick,
		aiDetectedActionItemsListRefetchKey,
		setAiDetectedActionItemsListRefetchKey,
	]);

	const highlightsEl = (
		<ActionItemsRecordingContext.Provider value={recordingContextValues}>
			<article
				key="highlights-el"
				className={cx(styles.highlightsContainer, {
					[styles.spaceForAskCopilotButton]: askCopilotEnabled,
				})}
			>
				<Highlights
					note={highlightsNoteKey}
					meetingRecording={meetingRecording}
					viewer={viewer}
					videoDuration={duration}
					onTimestampClick={onTimestampClick}
					onDislike={onDislike}
					onGenerateTemplate={onGenerateTemplate}
					chaptersRef={chaptersRef}
					thumbnailTrack={thumbnailTrack}
					narrowViewport={narrowViewport}
					trackLowLanguageConfidenceScoreBannerShown={trackLowLanguageConfidenceScoreBannerShown}
				/>
			</article>
		</ActionItemsRecordingContext.Provider>
	);

	const chaptersComponent = (
		<ChaptersPanel
			key="chapters-panel"
			ref={chaptersRef}
			recordingId={meetingRecording.id}
			chapters={meetingRecording.activeSummary?.chapters ?? []}
			onTimestampClick={onTimestampClick}
			onDislike={onDislike}
			videoDuration={duration}
			thumbnailTrack={thumbnailTrack}
			userVideoProgress={meetingRecording.userVideoProgress}
			mode={!note?.viewerCanEdit ? "readonly" : "edit"}
			recordingTitle={meetingRecording.title}
			chaptersScrollDisabled={fromNote || !isLoggedInSomewhere}
		/>
	);

	const transcriptEl = (
		<Transcript
			key="transcript-el"
			ref={transcriptRef}
			note={note}
			narrowViewport={narrowViewport}
			fromNote={fromNote}
			meetingRecording={meetingRecording}
			viewer={viewer}
			transcriptDeletionReason={meetingRecording.transcriptDeletionReason}
			transcript={(meetingRecording.transcript || []) as SpeakerTranscriptSegment[]}
			onSegmentClick={onTranscriptSegmentClick}
			pauseVideo={pauseVideo}
			videoDuration={duration}
			closeModal={doCloseModal}
			makeSpaceForAskCopilotButton={askCopilotEnabled}
			mode={mode}
			setMode={setMode}
			trackLowLanguageConfidenceScoreBannerShown={trackLowLanguageConfidenceScoreBannerShown}
		/>
	);

	const clipsEl = (
		<ClipsPanel
			key="clips-el"
			meetingRecording={meetingRecording}
			onClipSelect={onClipSelect}
			className={cx({ [styles.spaceForAskCopilotButton]: askCopilotEnabled })}
		/>
	);

	const statsEl = <StatsPanel meetingRecording={statsMeetingRecording} note={statsNoteKey} viewer={viewer} />;

	const tabOptions = React.useMemo(() => {
		if (meetingRecording.clips?.length) {
			return [HIGHLIGHTS_TAB, TRANSCRIPT_TAB, CLIPS_TAB, STATS_TAB] as const;
		} else {
			return [HIGHLIGHTS_TAB, TRANSCRIPT_TAB, STATS_TAB] as const;
		}
	}, [meetingRecording.clips?.length]);

	const mobileTabOptions = React.useMemo(() => {
		const hasClips = !!meetingRecording.clips?.length;

		if (hasClips) {
			return [HIGHLIGHTS_TAB, TRANSCRIPT_TAB, CLIPS_TAB, STATS_TAB] as const;
		} else {
			return [HIGHLIGHTS_TAB, TRANSCRIPT_TAB, STATS_TAB] as const;
		}
	}, [meetingRecording.clips?.length]);

	React.useEffect(() => {
		// If a user deletes the last clip on a video the tab will disappear but the ClipsPanel component
		// will still be rendering. So we need to automatically switch tabs to unmount the ClipsPanel component.
		if (activeTab === "Clips" && !meetingRecording.clips?.length) {
			setActiveTab("Highlights");
		}
		if (mobileActiveTab === "Clips" && !meetingRecording.clips?.length) {
			setMobileActiveTab("Highlights");
		}
	}, [activeTab, meetingRecording.clips?.length, mobileActiveTab]);

	const [hasUsedAskCopilot, setHasUsedAskCopilot] = React.useState(store.get(ASK_COPILOT_NEW_USER_KEY, false));

	const dismissShininess = React.useCallback(() => {
		setHasUsedAskCopilot(true);
		store.set(ASK_COPILOT_NEW_USER_KEY, true);
	}, []);

	const trackAskCopilotOpened = useTrackAskCopilotOpened();
	const openAskCopilot = React.useCallback(() => {
		if (!askCopilotEnabled) return;

		dismissShininess();
		setAskCopilotOpen(true);
		trackAskCopilotOpened("Transcript modal", {
			// eslint-disable-next-line camelcase
			recording_id: meetingRecording.id,
		});
	}, [askCopilotEnabled, dismissShininess, meetingRecording, trackAskCopilotOpened]);
	const closeAskCopilot = React.useCallback(() => setAskCopilotOpen(false), []);

	const recordingContextValue = React.useMemo(
		() => ({
			recordingId: meetingRecording.id,
			copySection: copyRecordingSection,
			openClipCutModal,
		}),
		[meetingRecording.id, copyRecordingSection, openClipCutModal],
	);

	if (isRegenerating || isGeneratingRecapTemplate || meetingRecording.regenerationForLanguageIsInProgress) {
		return (
			<TranscriptModalRegenerating isNarrow={Boolean(narrowViewport)} className={styles.generatingInModal}>
				<DefaultVideoPreview templateType={recapTemplateType} />
			</TranscriptModalRegenerating>
		);
	}

	if (meetingRecording.redactionIsInProgress) {
		return (
			<TranscriptModalRegenerating isNarrow={Boolean(narrowViewport)} className={styles.generatingInModal}>
				<RedactingPreview meetingRecording={meetingRecording} />
			</TranscriptModalRegenerating>
		);
	}

	if (narrowViewport) {
		const mobileContent = (
			<>
				<ToggleGroup
					key="mobile-toggle-group"
					className={styles.narrowWidthToggleGroup}
					options={mobileTabOptions}
					value={mobileActiveTab}
					onValueChange={onMobileValueChange}
				/>
				{mobileActiveTab === "Highlights" && highlightsEl}
				{mobileActiveTab === "Transcript" && transcriptEl}
				{mobileActiveTab === "Clips" && clipsEl}
				{mobileActiveTab === "Stats" && statsEl}
			</>
		);

		return (
			<div
				key="preview"
				className={cx(styles.preview, styles.forceNarrow, {
					[styles.previewPublicView]: isPublic && !isLoggedInSomewhere,
					[styles.fromNote]: fromNote,
				})}
			>
				<RecordingContext.Provider value={recordingContextValue}>
					<div key="metadata" className={styles.metadata}>
						{meetingRecording.meetingRecordingVideoDeletionReason ? recordingUnavailable : children}
						{mobileContent}
					</div>
				</RecordingContext.Provider>
			</div>
		);
	}

	const contextId = meetingRecording.id;

	const leftPanel =
		askCopilotOpen && contextId ? (
			<AskFellowPanel contextId={contextId} compactLandingPage={true} onClose={closeAskCopilot} />
		) : (
			<div className={styles.leftPanel}>
				{!(mode === "redacting" && activeTab === "Transcript") && (
					<ToggleGroup
						className={styles.leftPanelToggleGroup}
						itemClassName={styles.leftPanelToggleGroupItem}
						options={tabOptions}
						value={activeTab}
						onValueChange={onValueChange}
					/>
				)}
				{activeTab === "Highlights" && highlightsEl}
				{activeTab === "Transcript" && transcriptEl}
				{activeTab === "Clips" && clipsEl}
				{activeTab === "Stats" && statsEl}
				{askCopilotEnabled && mode !== "redacting" && (
					<Button
						variant="primary-ai"
						className={cx(styles.askCopilotButton, {
							[styles.shiny]: !hasUsedAskCopilot,
						})}
						onClick={openAskCopilot}
					>
						<FontAwesomeIcon icon={faSparklingSpeechBubble} />
						&nbsp; Ask Fellow &nbsp;
						<AskCopilotChevronUp />
					</Button>
				)}
			</div>
		);

	return (
		<div
			key="preview"
			id={`transcript-modal-preview-${note?.id}`}
			className={cx(styles.preview, {
				[styles.previewPublicView]: isPublic && !isLoggedInSomewhere,
				[styles.fromNote]: fromNote,
			})}
		>
			<RecordingContext.Provider value={recordingContextValue}>
				{leftPanel}
				<div key="metadata" className={styles.metadata}>
					{meetingRecording.meetingRecordingVideoDeletionReason ? recordingUnavailable : children}
					{chaptersComponent}
				</div>
			</RecordingContext.Provider>
		</div>
	);
});

export const transcriptModalViewerFragment = graphql`
	fragment TranscriptModal_viewer on Viewer @argumentDefinitions(isPublic: { type: "Boolean!" }) {
		videoPlaybackSettings {
			...TranscriptVideo_videoPlaybackSettings
		}
		account {
			__typename
			id
			name @include(if: $isPublic)
			logo @include(if: $isPublic)
			allowRecapDownload
			allowRecapEngagementStats
		}
		user {
			isGuest
		}
		...HighlightsPanel_viewer
		...MeetingContainerModalNote_viewer @skip(if: $isPublic)
		...NoteHeader_viewer @skip(if: $isPublic)
		...MeetingPhaseContext_viewer @skip(if: $isPublic)
		...TranscriptPanel_Transcript_viewer @skip(if: $isPublic)
		...StatsPanel_viewer @skip(if: $isPublic)
	}
`;

function RecordingsRenderer({
	note: noteKey,
	highlightsNoteKey,
	statsNoteKey,
	recordings,
	closeModal,
	initialMeetingRecordingId,
	initialVideoTimestamp,
	viewer: viewerKey,
	openedFromMiniplayerClick,
	initialTranscriptModalSubTab,
	videoOriginRefs: initialVideoOriginRefs,
	openedTab,
}: {
	note: TranscriptModal_RecordingsRenderer_note$key | null;
	highlightsNoteKey: HighlightsPanel_note$key | null;
	statsNoteKey: StatsPanel_note$key | null;
	recordings: TranscriptModalNote["meetingRecordings"];
	closeModal: () => void;
	initialMeetingRecordingId?: string;
	initialVideoTimestamp?: number;
	initialTranscriptModalSubTab?: TranscriptModalContentTab;
	viewer: TranscriptModal_viewer$key;
	openedFromMiniplayerClick?: boolean;
	videoOriginRefs?: VideoOriginRefs;
	openedTab?: CurrentTabType;
}) {
	const note = useFragment(
		graphql`
			fragment TranscriptModal_RecordingsRenderer_note on INote @argumentDefinitions(public: { type: "Boolean!" }) {
				id
				__typename

				... on Note {
					meetingRecordings {
						id
					}
				}

				...TranscriptModalContent_note
				...MeetingContainerModalNote_note @skip(if: $public)
				...MeetingContainerTabs_note @skip(if: $public)
				...TranscriptModalHeader_note @skip(if: $public)
			}
		`,
		noteKey,
	);

	const viewer = useFragment(transcriptModalViewerFragment, viewerKey);

	const longestMeetingRecording = useLongestMeetingRecording(recordings);
	const [currentTab, setCurrentTab] = React.useState<CurrentTabType>(
		Boolean(longestMeetingRecording) ? "recap" : "notes",
	);

	React.useEffect(() => {
		if (openedTab) {
			setCurrentTab(openedTab);
		}
	}, [openedTab]);

	const isPublic = useIsPublic();

	const isLoggedInSomewhere = AccountStore.getAll().length > 0 || !isPublic;

	const videoRef = React.useRef<HTMLVideoElement>(null);
	const chaptersRef = React.useRef<ChaptersPanelRef>(null);
	const contentRef = React.useRef<TranscriptModalContentRef>(null);
	const [duration, setDuration] = React.useState<number | null>(null);

	const publicContext = usePublicAccessKey();

	const tabs = React.useMemo(() => {
		return recordings
			?.filter(
				(
					recording,
				): recording is typeof recording & {
					meetingCallStart: string;
					meetingCallEnd: string;
				} => !!recording.meetingCallStart && !!recording.meetingCallEnd,
			)
			.map((recording, i) => {
				return {
					label: `Part ${i + 1}`,
					value: recording.id,
				};
			});
	}, [recordings]);

	const [selectedRecordingId, setSelectedRecordingId] = React.useState(
		initialMeetingRecordingId ?? longestMeetingRecording?.id,
	);

	const onTimestampClick = useOnTimestampClick(
		note?.meetingRecordings?.map(r => r.id) ?? [],
		setSelectedRecordingId,
		videoRef,
		chaptersRef,
		setCurrentTab,
	);

	const selectedRecording = recordings?.find(recording => recording.id === selectedRecordingId);

	React.useEffect(() => {
		contentRef.current?.transcriptRef.current?.closeCommentsPanel();
	}, [selectedRecordingId]);

	const addedOrAvailableUntil = useAddedOrAvailableUntil(selectedRecording ?? null);

	const [thumbnailTrack, setThumbnailTrack] = React.useState<TextTrack | null>(null);

	const { analyticsFingerprint, setNoteId, setMeetingRecordingSummaryId } = React.useContext(TranscriptModalContext);

	// Set the noteId and meetingRecordingSummaryId in context for analytics tracking across the modal
	React.useEffect(() => {
		setNoteId(note?.id ?? null);
		setMeetingRecordingSummaryId(selectedRecording?.activeSummary?.id);
	}, [note, selectedRecording?.activeSummary?.id, setMeetingRecordingSummaryId, setNoteId]);

	// When opening the modal from an already existing video, which can happen with the new Meeting Container
	// We want this component to behave differently. When closing the mini-player we want to replicate the video's state,
	// to the original video and close it if an original video is present
	const [videoOriginRefs, setVideoOriginRefs] = React.useState<VideoOriginRefs | undefined>(initialVideoOriginRefs);
	const refreshOriginVideoState = React.useCallback(
		(replicatePlayState?: boolean) => {
			if (!videoOriginRefs?.videoRef?.current || !videoRef?.current) return;
			videoOriginRefs.videoRef.current.currentTime = videoRef.current.currentTime;
			if (!videoRef.current.paused && replicatePlayState) {
				videoOriginRefs.videoRef.current.play();
			}
		},
		[videoOriginRefs?.videoRef, videoRef],
	);

	const onMiniplayerClose = React.useCallback(
		(_?: React.MouseEvent<HTMLElement>, goBackToOriginalVideo?: boolean) => {
			// If we have references to the video origin we want to update and show that
			if (videoOriginRefs?.videoRef?.current && videoRef?.current) {
				if (videoOriginRefs.videoRef.current.parentElement) {
					videoOriginRefs.videoRef.current.parentElement.style.visibility = "visible";
				}
				refreshOriginVideoState(goBackToOriginalVideo);
			}
			// If we want to go back to the original video we want to scroll that into view
			// And if the video is being shown tell it to show it
			if (goBackToOriginalVideo && videoOriginRefs?.containerRef?.current) {
				// We only want to scroll the content into view if the video is not visible
				// TODO: Check for that
				const videoBoundingRect = videoOriginRefs.videoRef?.current?.getBoundingClientRect();
				const videoIsVisible =
					videoBoundingRect && videoBoundingRect.top >= 0 && videoBoundingRect.bottom <= window.innerHeight;
				if (!videoIsVisible) videoOriginRefs.containerRef.current.scrollIntoView({ behavior: "smooth" });
				// If we have the origin note being rendered but not the video, we need to let the note know
				// That we want to show the video
				if (videoRef.current && !videoOriginRefs?.videoRef?.current && selectedRecordingId) {
					// We first need to update the local record for the video progress. When closing the mini-player
					// That value will be updated through a mutation, but things will happen in parallel here
					// And we don't want to wait for that to finish before starting the new video.
					commitLocalUpdate(environment, store => {
						const recordingStoreRecord = store.get(selectedRecordingId);
						if (!recordingStoreRecord) return;
						recordingStoreRecord.setValue(videoRef.current?.currentTime, "userVideoProgress");
						if (!videoOriginRefs.containerRef?.current || !videoRef.current) return;
						videoOriginRefs.containerRef.current.dispatchEvent(
							new CustomEvent(RECORDING_SHOW_VIDEO, {
								detail: {
									playing: !videoRef.current.paused,
									currentTime: videoRef.current.currentTime,
								},
							}),
						);
					});
				}
			}
			closeModal();
		},
		[
			closeModal,
			refreshOriginVideoState,
			videoOriginRefs?.videoRef,
			videoOriginRefs?.containerRef,
			videoRef,
			selectedRecordingId,
		],
	);

	const [isMiniPlayerActive, setIsMiniPlayerActive] = React.useState<boolean>(Boolean(initialVideoOriginRefs));
	const toggleMiniPlayer = React.useCallback(() => {
		const hasOriginRef = Boolean(videoOriginRefs?.videoRef?.current) || Boolean(videoOriginRefs?.containerRef?.current);
		if (!hasOriginRef || !videoRef.current) {
			setIsMiniPlayerActive(prev => !prev);
			return;
		}
		onMiniplayerClose(undefined, true);
	}, [videoOriginRefs?.videoRef, videoOriginRefs?.containerRef, videoRef, onMiniplayerClose]);

	React.useEffect(() => {
		if (isMiniPlayerActive) {
			dispatchAnalyticsTrack("[AI Copilot] Mini Player Opened", {
				fingerprint: analyticsFingerprint,
				source: isPublic ? "Public View" : "Modal",
			});
		}
	}, [analyticsFingerprint, isMiniPlayerActive, isPublic]);

	React.useEffect(() => {
		if (selectedRecording) {
			dispatchAnalyticsTrack("[AI Copilot] Recap Viewed", {
				fingerprint: analyticsFingerprint,
				source: isPublic ? "Public View" : "Modal",
				/* eslint-disable camelcase */
				note_id: note?.id,
				meeting_recording_summary_id: selectedRecording.activeSummary?.id,
				has_external_attendees: selectedRecording?.hasExternalAttendees,
				/* eslint-enable camelcase */
			});
		}
	}, [analyticsFingerprint, isPublic, note?.id, selectedRecording]);

	const [mode, setMode] = React.useState<TranscriptMode>("default");

	const onVideoTimeUpdate = React.useCallback(() => {
		if (!videoRef.current || !contentRef.current) return;

		const { currentTime } = videoRef.current;
		const { transcriptRef, chaptersRef } = contentRef.current;

		transcriptRef.current?.handleTimeUpdate(currentTime);
		chaptersRef.current?.handleTimeUpdate(currentTime);
	}, [contentRef, videoRef]);

	const onVideoLoadedMetadata = React.useCallback(() => {
		if (!videoRef.current) return;
		if (isNaN(videoRef.current.duration)) return;

		const textTracks = videoRef.current.textTracks;
		for (let i = 0; i < textTracks.length; i += 1) {
			const track = textTracks[i];
			if (track.kind === "metadata" && track.label === "thumbnails") {
				setThumbnailTrack(track);
				break;
			}
		}

		setDuration(videoRef.current.duration);
	}, [setDuration, videoRef]);

	const handleEscapeKeyDown = React.useCallback(
		async (event: React.KeyboardEvent<Element>) => {
			if (event.key !== "Escape" || mode === "default") return;
			event.preventDefault();
			const discardConfirmed = await confirmDiscardRedactions();
			if (!discardConfirmed) return;
			setMode("default");
		},
		[mode],
	);

	const [startTimestamp, _] = React.useState<number>(Date.now());
	const onClose = React.useCallback(async () => {
		if (mode === "redacting") {
			const discardRedactions = await confirmDiscardRedactions();
			if (!discardRedactions) return;
		}
		const timeElapsed = Date.now() - startTimestamp;
		if (timeElapsed >= MIN_TIME_FOR_SURVEY && !store.get(SHOWED_SURVEY_KEY, false)) {
			setTimeout(() => {
				if (!window.Intercom) return;
				window.Intercom("startSurvey", INTERCOM_SURVEY_ID);
				store.set(SHOWED_SURVEY_KEY, true);
			}, DELAY_TO_SHOW_THE_SURVEY);
		}
		dispatchAnalyticsTrack("[AI Copilot] Transcript modal closed", {
			videoCurrentTime: videoRef.current?.currentTime,
			videoDuration: videoRef.current?.duration,
			videoPlaybackSpeed: videoRef.current?.playbackRate,
			fingerprint: analyticsFingerprint,
		});
		closeModal();
	}, [analyticsFingerprint, closeModal, mode, startTimestamp]);

	const onClipSelect = React.useCallback(
		(clipId: string) => {
			if (videoRef.current && !videoRef.current.paused) {
				// If the recording video is currently playing, pause it, so user can play the clip video
				videoRef.current.pause();
			}

			openClipModal(clipId);
		},
		[videoRef],
	);

	const openClipTrimmer = React.useCallback(() => {
		if (videoRef.current && !videoRef.current.paused) {
			// If the recording video is currently playing, pause it, so user can play the clip video
			videoRef.current.pause();
		}

		const videoTime = videoRef.current?.currentTime;
		contentRef.current?.openClipCutModal(videoTime);
	}, []);

	const [playedRecordings, setPlayedRecordings] = React.useState<string[]>([]);
	const onPlayVideo = React.useCallback(() => {
		const playedVideo = selectedRecordingId;

		closeClipModal();

		if (!playedVideo || playedRecordings.includes(playedVideo)) return;

		setPlayedRecordings(prev => [...prev, playedVideo]);

		if (isPublic && publicContext) {
			if (publicContext.type === "recording") {
				fetch("/public/meeting_recordings/view/recording/", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"x-csrftoken": window.CSRF_TOKEN,
					},
					body: JSON.stringify({
						accessKey: publicContext.accessKey,
						recordingId: playedVideo,
					}),
				});
			}
		}
	}, [selectedRecordingId, playedRecordings, isPublic, publicContext]);

	const narrowViewport = useIsNarrowViewport();

	// Using state here because React explicitly doesn't make any guarantees that
	// useMemo won't be recomputed for whatever reason.
	const [videoPlayerContainer] = React.useState(() => {
		const videoPlayerDiv = document.createElement("div");
		videoPlayerDiv.style.width = "100%";
		videoPlayerDiv.style.height = "100%";
		return videoPlayerDiv;
	});

	const onDimmerClickEnterMiniPlayer = React.useCallback(() => {
		// If the video is currently playing, enter the mini player. Otherwise
		// close the modal.
		if (!videoRef.current || videoRef.current.paused || mode !== "default") {
			onClose();
		} else {
			toggleMiniPlayer();
		}
	}, [toggleMiniPlayer, onClose, videoRef, mode]);

	function handleVideoContainerRef(el: HTMLElement | null): void {
		// When we get a new ref for where the video element is supposed to go, we
		// move the DOM element there. This way all the internal state of the video
		// element is maintained, meaning transitions between modal and mini player
		// are seamless.
		// Do nothing if the element is already in the right place, to avoid unnecessary re-renders.
		// Otherwise, fullscreen mode will break when device orientation changes.

		if (!el || el?.contains(videoPlayerContainer)) {
			return;
		}
		el?.appendChild(videoPlayerContainer);
	}

	const addToRecordingLibrary = React.useCallback(() => {
		if (!selectedRecording) return;

		const lastAccountAccessed = AccountStore.getLastAccessed();
		if (!lastAccountAccessed) return;

		if (publicContext?.type === "recording") {
			window.open(
				`${lastAccountAccessed.uri}/public/meeting_recordings/add-to-library/${publicContext.accessKey}`,
				"_blank",
			);
		}
	}, [selectedRecording, publicContext]);

	const [videoCollapsed, setVideoCollapsed] = React.useState(false);
	const toggleVideoCollapsed = React.useCallback(() => setVideoCollapsed(!videoCollapsed), [videoCollapsed]);

	const onDeleteExecuted = React.useCallback(
		(type: DeletionType) => {
			if (type === "EVERYTHING") {
				closeModal();
			} else if (type === "RECAP") {
				const firstOtherRecording = recordings?.find(recording => recording.id !== selectedRecordingId);
				if (firstOtherRecording) {
					setSelectedRecordingId(firstOtherRecording.id);
				} else {
					setSelectedRecordingId(undefined);
					setCurrentTab("notes");
				}
			}
		},
		[recordings, closeModal, selectedRecordingId],
	);

	let content;
	if (!isPublic && isMiniPlayerActive && selectedRecording) {
		content = (
			<MiniPlayer
				title={selectedRecording.title || ""}
				onClose={onMiniplayerClose}
				toggleMiniPlayer={toggleMiniPlayer}
				collapsed={selectedRecording?.mediaType === "AUDIO"}
			>
				<div ref={handleVideoContainerRef} className={styles.miniPlayer} />
			</MiniPlayer>
		);
	} else {
		let modalWidth,
			modalHeight,
			onModalClose: typeof onDimmerClickEnterMiniPlayer = onClose;
		if (isPublic) {
			modalWidth = "100vw";
			modalHeight = "100vh";
		} else if (narrowViewport) {
			modalWidth = modalHeight = "100%";
		} else {
			modalWidth = "67rem";
			modalHeight = "53rem";
			onModalClose = onDimmerClickEnterMiniPlayer;
		}

		let headerActions = null;

		if (!isLoggedInSomewhere) {
			const rootURL = window.ROOT_URL ?? "https://fellow.app";
			const signupLink =
				publicContext?.type === "recording"
					? `${rootURL}/auth/signup/?recap_key=${publicContext.accessKey}`
					: `${rootURL}/auth/signup/`;

			headerActions = (
				<>
					{narrowViewport ? (
						<Menu key="actions-menu">
							<Menu.Trigger>
								<IconButton icon={faEllipsisV} className={styles.moreActionsIcon} />
							</Menu.Trigger>
							<Menu.Content align="end">
								<Menu.ItemLink href="/auth/login/" data-test-id="transcript-modal-login-button">
									Log in
								</Menu.ItemLink>
							</Menu.Content>
						</Menu>
					) : (
						<AnchorButton
							key="login-button"
							variant="secondary"
							href="/auth/login/"
							data-test-id="transcript-modal-login-button"
						>
							Log in
						</AnchorButton>
					)}
					<AnchorButton
						key="continue-button"
						variant="primary"
						size={narrowViewport ? "slim" : "normal"}
						href={signupLink}
						data-test-id="transcript-modal-header-continue"
					>
						Continue{!narrowViewport && " to collaborate"}
					</AnchorButton>
				</>
			);
		} else if (isPublic) {
			const isExternalRecording =
				selectedRecording &&
				selectedRecording?.__typename === "MeetingRecording" &&
				selectedRecording.ownerAccount.id !== viewer.account.id;

			headerActions = (
				<>
					{/* Add the recording to shared library */}
					{isPublic && isExternalRecording && !narrowViewport && (
						<Button
							variant="primary"
							size={narrowViewport ? "slim" : "normal"}
							data-test-id="transcript-modal-header-add-to-library"
							onClick={addToRecordingLibrary}
						>
							Add to my library
						</Button>
					)}
				</>
			);
		}

		content = (
			<Modal
				key="transcript-modal"
				onClose={onModalClose}
				className={cx(styles.modal, { [styles.publicModal]: isPublic })}
				overlayClassName={cx(styles.overlayReducedPadding, {
					[styles.overlayNoPadding]: isPublic,
				})}
				width={modalWidth}
				height={modalHeight}
				onKeyDown={handleEscapeKeyDown}
			>
				<Modal.Header className={styles.modalHeader}>
					<div className={styles.noteHeaderWrapper}>
						{isPublic && !narrowViewport ? (
							<div className={styles.publicModalHeaderLeft}>
								{!isLoggedInSomewhere ? (
									<div className={styles.fellowIcon}>
										<FellowIcon />
									</div>
								) : (
									viewer.account && (
										<Avatar
											className={styles.ownerAccountAvatar}
											width="36px"
											user={{
												id: "",
												image: viewer.account.logo,
												fullName: viewer.account.name,
											}}
										/>
									)
								)}
								<div>
									<Modal.Title>
										{!isLoggedInSomewhere && "Fellow Recording: "}
										{selectedRecording?.title}
									</Modal.Title>
									{viewer.account && selectedRecording && (
										<div className={styles.publicShareInfoContainer}>
											Shared by {viewer.account.name} {addedOrAvailableUntil && <> · {addedOrAvailableUntil}</>}
										</div>
									)}
								</div>
							</div>
						) : !note || isPublic ? (
							<Modal.Title>{selectedRecording?.title}</Modal.Title>
						) : (
							<Modal.Title>
								<TranscriptModalHeader
									noteHeaderNoteKey={note}
									viewer={viewer}
									closeModal={closeModal}
									recordingId={selectedRecordingId}
									onDeleteExecuted={onDeleteExecuted}
								/>
							</Modal.Title>
						)}
						<Modal.HeaderActions onClose={isPublic ? undefined : onClose} className={styles.modalHeaderActions}>
							{headerActions}
						</Modal.HeaderActions>
					</div>
					{!isPublic && note && (
						<MeetingContainerTabs
							currentTab={currentTab}
							setCurrentTab={setCurrentTab}
							className={styles.tabs}
							noteKey={note}
						/>
					)}
				</Modal.Header>

				<Modal.Body noPadding className={styles.recapModalBody}>
					{currentTab == "recap" || isPublic ? (
						selectedRecording && (
							<TranscriptModalContent
								key="transcript-modal-content"
								note={note}
								highlightsNoteKey={highlightsNoteKey}
								statsNoteKey={statsNoteKey}
								viewer={viewer}
								ref={contentRef}
								videoRef={videoRef}
								chaptersRef={chaptersRef}
								onTimestampClick={onTimestampClick}
								meetingRecording={selectedRecording}
								videoPlaybackSettings={viewer.videoPlaybackSettings}
								duration={duration}
								thumbnailTrack={thumbnailTrack}
								initialTab={initialTranscriptModalSubTab ?? (initialVideoTimestamp != null ? "Transcript" : undefined)}
								onClipSelect={onClipSelect}
								closeModal={closeModal}
								mode={mode}
								setMode={setMode}
								fromNote={!note}
								parts={tabs}
								setSelectedRecordingId={setSelectedRecordingId}
								allowRecapDownload={viewer.account.allowRecapDownload}
							>
								<div key="container-el" style={{ zIndex: 1 }} ref={handleVideoContainerRef} />
								<CollapseButton
									isCollapsed={videoCollapsed}
									onClick={toggleVideoCollapsed}
									mediaType={selectedRecording.mediaType}
								/>
							</TranscriptModalContent>
						)
					) : (
						<MeetingContainerModalNote
							note={note}
							viewer={viewer}
							accountId={viewer.account.id}
							closeModal={closeModal}
							onTimestampClick={onTimestampClick}
						/>
					)}
				</Modal.Body>
			</Modal>
		);
	}

	React.useEffect(() => {
		if (!videoRef.current || !videoOriginRefs?.videoRef) return;
		const originalVideoWasPlaying = !videoOriginRefs.videoRef.current?.paused;
		videoOriginRefs.videoRef.current?.pause();
		if (videoOriginRefs.videoRef.current?.parentElement) {
			videoOriginRefs.videoRef.current.parentElement.style.visibility = "hidden";
		}
		if (originalVideoWasPlaying) {
			videoRef.current.play();
		}
	}, [videoRef]); // eslint-disable-line react-hooks/exhaustive-deps
	// We don't need to run this use effect when videoOriginRefs.videoRef changes

	const onNewRecordingVideoCreated = React.useCallback(
		(event: RecordingVideoPlayerReadyEvent) => {
			if (
				!videoRef.current ||
				!event.detail.meetingRecordingId ||
				!event.detail.updateVideoState ||
				!event.detail.videoRef ||
				!event.detail.containerRef
			)
				return;
			if (event.detail.meetingRecordingId !== selectedRecordingId) return;
			setVideoOriginRefs({ containerRef: event.detail.containerRef, videoRef: event.detail.videoRef });
			if (openedFromMiniplayerClick && event.detail.videoRef.current?.parentElement) {
				event.detail.videoRef.current.parentElement.style.visibility = "hidden";
				return;
			}
			event.detail.updateVideoState(!videoRef.current.paused, videoRef.current.currentTime);
			closeModal();
		},
		[videoRef, selectedRecordingId, closeModal, openedFromMiniplayerClick],
	);

	React.useEffect(() => {
		window.addEventListener(RECORDING_VIDEO_PLAYER_READY, onNewRecordingVideoCreated);
		return () => window.removeEventListener(RECORDING_VIDEO_PLAYER_READY, onNewRecordingVideoCreated);
	}, [onNewRecordingVideoCreated]);

	const onRecordingStartPlaying = React.useCallback(
		(event: RecordingVideoStartPlayingEvent) => {
			if (!videoRef.current || !event.detail.meetingRecordingId) return;
			if (event.detail.meetingRecordingId === selectedRecordingId) return;
			onMiniplayerClose();
		},
		[videoRef, selectedRecordingId, onMiniplayerClose],
	);

	React.useEffect(() => {
		window.addEventListener(RECORDING_VIDEO_START_PLAYING, onRecordingStartPlaying);
		return () => window.removeEventListener(RECORDING_VIDEO_START_PLAYING, onRecordingStartPlaying);
	}, [onRecordingStartPlaying]);

	useSessionManager(videoRef.current, selectedRecording?.id ?? null, null);

	return (
		<>
			{ReactDOM.createPortal(
				selectedRecording && (
					<TranscriptVideo
						key={selectedRecording.id}
						ref={videoRef}
						meetingRecording={selectedRecording}
						initialVideoTimestamp={initialVideoTimestamp}
						videoPlaybackSettings={viewer.videoPlaybackSettings}
						isMiniPlayerActive={isMiniPlayerActive}
						toggleMiniPlayer={toggleMiniPlayer}
						thumbnailTrack={thumbnailTrack}
						openClipTrimmer={openClipTrimmer}
						inClippingMode={false}
						parts={tabs}
						setSelectedRecordingId={setSelectedRecordingId}
						allowRecapDownload={viewer?.account.allowRecapDownload}
						// The following are videoProps
						onPlay={onPlayVideo}
						onTimeUpdate={onVideoTimeUpdate}
						onLoadedMetadata={onVideoLoadedMetadata}
						isCollapsed={videoCollapsed}
						setIsCollapsed={setVideoCollapsed}
					>
						<track label="Subtitles" kind="subtitles" src={selectedRecording.subtitlesVttUrl} />
						{selectedRecording.thumbnailVttUrl && (
							<track default label="thumbnails" kind="metadata" src={selectedRecording.thumbnailVttUrl} />
						)}
					</TranscriptVideo>
				),
				videoPlayerContainer,
			)}
			{content}
		</>
	);
}

export function LoadingFallback() {
	return <LoadingSpinner delay={500} />;
}

type ModalCommonProps = {
	initialVideoTimestamp?: number;
	openedFromMiniplayerClick?: boolean;
	videoOriginRefs?: VideoOriginRefs;
	initialTranscriptModalSubTab?: TranscriptModalContentTab;
	openedTab?: CurrentTabType;
	timestampStart?: number | null;
	timestampEnd?: number | null;
};

function TranscriptModalRendererWithRecordingId({
	close,
	recordingId,
	initialVideoTimestamp,
	openedFromMiniplayerClick,
	initialTranscriptModalSubTab,
	videoOriginRefs,
	openedTab,
	timestampStart,
	timestampEnd,
}: ModalProps<unknown> &
	ModalCommonProps & {
		recordingId: string;
	}) {
	const data = useLazyLoadQuery<TranscriptModalRecordingIdQuery>(
		graphql`
			query TranscriptModalRecordingIdQuery($id: ID!) {
				node(id: $id) {
					__typename
					... on MeetingRecording {
						id
						title
						createdAt
						meetingCallStart
						meetingCallEnd
						meetingVideoAndTranscriptionDownloadUrl
						meetingRecordedVideoUrl
						subtitlesVttUrl
						thumbnailVttUrl
						isFavourite
						viewerCanDelete
						hasExternalAttendees
						activeSummary {
							id
						}
						clips {
							id
							subtitlesVttUrl
							thumbnailVttUrl
							...TranscriptVideo_meetingRecording @arguments(public: false)
						}
						note {
							...TranscriptModal_RecordingsRenderer_note @arguments(public: false)
						}
						...TranscriptModal_meetingRecording @arguments(public: false)
						...TranscriptVideo_meetingRecording @arguments(public: false)
						...utils_useLongestMeetingRecording_meetingRecordings
						...utils_AddedOrAvailableUntil_meetingRecording
						...utils_useGetURL_recording
						...RecapHistory_meetingRecording
						...Timeline_meetingKeyEvents
						ownerAccount {
							id
						}
						mediaType
					}
				}

				viewer {
					...TranscriptModal_viewer @arguments(isPublic: false)
				}

				account {
					billingAccount {
						hasAccessToHistory: hasAccessToFeature(feature: history)
						historyLimitInDays: usageLimitForFeature(feature: history)
					}
				}
			}
		`,
		{ id: recordingId },
		{ fetchPolicy: "network-only" },
	);

	const {
		account: {
			billingAccount: { hasAccessToHistory, historyLimitInDays },
		},
	} = data;

	const isMeetingRecording = data.node?.__typename === "MeetingRecording";

	const recording = isMeetingRecording ? data.node : null;

	const isPaywalled = usePaywallCheck(recording?.createdAt, hasAccessToHistory, historyLimitInDays);

	if (!isMeetingRecording || !recording) {
		return null;
	}

	if (isPaywalled) {
		showFeatureModal("history");
		return <></>;
	}

	return (
		<TranscriptModalContextProvider timestampStart={timestampStart} timestampEnd={timestampEnd}>
			<RecordingsRenderer
				recordings={[recording]}
				closeModal={close}
				initialMeetingRecordingId={recording.id}
				initialVideoTimestamp={initialVideoTimestamp}
				viewer={data.viewer}
				note={data.node.note}
				highlightsNoteKey={null}
				statsNoteKey={null}
				openedFromMiniplayerClick={openedFromMiniplayerClick}
				videoOriginRefs={videoOriginRefs}
				initialTranscriptModalSubTab={initialTranscriptModalSubTab}
				openedTab={openedTab}
			/>
		</TranscriptModalContextProvider>
	);
}

export const transcriptNoteFragment = graphql`
	fragment TranscriptModal_note on Note @argumentDefinitions(public: { type: "Boolean!" }) {
		id
		groupName
		eventStart
		createdAt
		title
		meetingRecordings {
			__typename
			id
			title
			meetingCallStart
			meetingCallEnd
			meetingVideoAndTranscriptionDownloadUrl
			meetingRecordedVideoUrl
			subtitlesVttUrl
			thumbnailVttUrl
			isFavourite @skip(if: $public)
			viewerCanDelete
			hasExternalAttendees
			activeSummary {
				id
			}
			clips @skip(if: $public) {
				id
				subtitlesVttUrl
				thumbnailVttUrl
				...TranscriptVideo_meetingRecording @arguments(public: $public)
			}
			...TranscriptModal_meetingRecording @arguments(public: $public)
			...TranscriptVideo_meetingRecording @arguments(public: $public)
			...utils_useLongestMeetingRecording_meetingRecordings
			...utils_AddedOrAvailableUntil_meetingRecording
			...utils_useGetURL_recording
			...RecapHistory_meetingRecording
			...Timeline_meetingKeyEvents
			ownerAccount {
				id
			}
			mediaType
		}
		...utils_useAutoRefetchMeetingRecording_note @skip(if: $public)
		...utils_useMeetingRecorderStatus_note @skip(if: $public)
		...HighlightsPanel_note
		...StatsPanel_note
		...TranscriptModalContent_note
	}
`;

function TranscriptModalRendererWithNote({
	close,
	noteNodeId,
	initialMeetingRecordingId,
	initialVideoTimestamp,
	openedFromMiniplayerClick,
	videoOriginRefs,
	initialTranscriptModalSubTab,
	openedTab,
}: ModalProps<unknown> &
	ModalCommonProps & {
		noteNodeId: string;
		initialMeetingRecordingId?: string;
		initialTranscriptModalSubTab?: TranscriptModalContentTab;
	}) {
	const isPublic = useIsPublic();
	const data = useLazyLoadQuery<TranscriptModalQuery>(
		graphql`
			query TranscriptModalQuery($id: ID!) {
				node: meetingRecordingNote(id: $id) {
					__typename
					... on Note {
						...TranscriptModal_note @arguments(public: false)
						...TranscriptModal_RecordingsRenderer_note @arguments(public: false)
					}
				}

				viewer {
					...TranscriptModal_viewer @arguments(isPublic: false)
				}

				account {
					billingAccount {
						hasAccessToHistory: hasAccessToFeature(feature: history)
						historyLimitInDays: usageLimitForFeature(feature: history)
					}
				}
			}
		`,
		{ id: noteNodeId },
		{ fetchPolicy: "network-only" },
	);

	const {
		account: {
			billingAccount: { hasAccessToHistory, historyLimitInDays },
		},
	} = data;

	const note = useFragment<TranscriptModal_note$key>(
		transcriptNoteFragment,
		data?.node?.__typename === "Note" ? data.node : null,
	);

	const isPaywalled = usePaywallCheck(note?.createdAt, hasAccessToHistory, historyLimitInDays);

	useAutoRefetchMeetingRecordings(!isPublic && note ? note : null);

	if (data?.node?.__typename !== "Note") {
		return <TranscriptPreviewFailedModal closeModal={close} />;
	}

	if (isPaywalled) {
		showFeatureModal("history");
		return <></>;
	}

	return (
		<TranscriptModalContextProvider>
			<RecordingsRenderer
				note={data.node}
				recordings={note?.meetingRecordings ?? []}
				closeModal={close}
				initialMeetingRecordingId={initialMeetingRecordingId}
				initialVideoTimestamp={initialVideoTimestamp}
				viewer={data.viewer}
				highlightsNoteKey={note}
				statsNoteKey={note}
				openedFromMiniplayerClick={openedFromMiniplayerClick}
				videoOriginRefs={videoOriginRefs}
				initialTranscriptModalSubTab={initialTranscriptModalSubTab}
				openedTab={openedTab}
			/>
		</TranscriptModalContextProvider>
	);
}

const TranscriptModalForNoteWithSuspense = withSuspense(<LoadingFallback />, TranscriptModalRendererWithNote);
const TranscriptModalForRecordingWithSuspense = withSuspense(
	<LoadingFallback />,
	TranscriptModalRendererWithRecordingId,
);
type VideoOriginRefs = {
	videoRef?: React.RefObject<HTMLVideoElement>;
	containerRef?: React.RefObject<HTMLDivElement>;
};

export interface OpenTranscriptModalProps {
	noteId?: string;
	recordingId?: string;
	timestamp?: number;
	openedFromMiniplayerClick?: boolean;
	videoOriginRefs?: VideoOriginRefs;
	openedTab?: CurrentTabType;
	initialTranscriptModalSubTab?: TranscriptModalContentTab;
	timestampEnd?: number | null;
}

export function openTranscriptModal({
	noteId,
	recordingId,
	timestamp,
	openedFromMiniplayerClick,
	videoOriginRefs,
	openedTab,
	initialTranscriptModalSubTab,
	timestampEnd,
}: OpenTranscriptModalProps) {
	if (!noteId && !recordingId) return;

	__globalModalContext?.open<null>(
		({ close }) => {
			if (noteId) {
				return (
					<TranscriptModalForNoteWithSuspense
						noteNodeId={noteId}
						close={close}
						initialMeetingRecordingId={recordingId}
						initialVideoTimestamp={timestamp}
						openedFromMiniplayerClick={openedFromMiniplayerClick}
						videoOriginRefs={videoOriginRefs}
						initialTranscriptModalSubTab={initialTranscriptModalSubTab}
						openedTab={openedTab}
					/>
				);
			}

			if (recordingId) {
				return (
					<TranscriptModalForRecordingWithSuspense
						recordingId={recordingId}
						close={close}
						initialVideoTimestamp={timestamp}
						openedFromMiniplayerClick={openedFromMiniplayerClick}
						videoOriginRefs={videoOriginRefs}
						initialTranscriptModalSubTab={initialTranscriptModalSubTab}
						openedTab={openedTab}
						timestampStart={timestamp}
						timestampEnd={timestampEnd}
					/>
				);
			}

			return <></>;
		},
		{ exclusiveId: "transcript-modal", persistOnNavigation: true },
	);
}

interface PublicClipModalProps {
	videoPlaybackSettings: TranscriptModal_PublicClipModal_videoPlaybackSettings$key;
	clip: TranscriptModal_PublicClipModal_clip$key;
	initialVideoTimestamp?: number;
}

function PublicClipModal({
	videoPlaybackSettings: videoPlaybackSettingsKey,
	clip: clipKey,
	initialVideoTimestamp,
}: PublicClipModalProps) {
	const { analyticsFingerprint } = React.useContext(TranscriptModalContext);

	const clip = useFragment(
		graphql`
			fragment TranscriptModal_PublicClipModal_clip on MeetingRecordingClip {
				id
				clipEnd
				clipStart
				summary
				title
				transcript
				ownerAccount {
					name
					logo
					allowRecapDownload
				}
				mediaType

				... on MeetingRecordingInterface {
					...TranscriptVideo_meetingRecording @arguments(public: true)
					...TranscriptPanel_Transcript_meetingRecording @arguments(public: true)
				}

				...utils_useGetURL_recording
			}
		`,
		clipKey,
	);

	const videoPlaybackSettings = useFragment(
		graphql`
			fragment TranscriptModal_PublicClipModal_videoPlaybackSettings on UserVideoPlaybackSettings {
				...TranscriptVideo_videoPlaybackSettings
			}
		`,
		videoPlaybackSettingsKey,
	);

	const videoRef = React.useRef<HTMLVideoElement>(null);
	const transcriptRef = React.useRef<TranscriptRef>(null);

	const [justCopied, setJustCopied] = React.useState(false);
	React.useEffect(() => {
		if (!justCopied) return;

		const timeout = window.setTimeout(() => {
			setJustCopied(false);
		}, 3000);
		return () => window.clearTimeout(timeout);
	}, [justCopied]);

	const getClipURL = useGetURL(clip);
	const copyLink = React.useCallback(() => {
		if (!getClipURL) return;
		const urlPromise = getClipURL();
		copyToClipboard(urlPromise).then(() => setJustCopied(true));
	}, [getClipURL]);

	const onVideoTimeUpdate = React.useCallback(() => {
		if (!videoRef.current) return;

		transcriptRef.current?.handleTimeUpdate(videoRef.current.currentTime);
	}, []);

	const onTranscriptSegmentClick = React.useCallback(
		(transcriptSegment: SpeakerTranscriptSegment) => {
			const video = videoRef.current;
			if (!video) return;

			// Subtract half a second to make sure you get the first word; the browser
			// might round this number.
			video.currentTime = Math.max(0, transcriptSegment.words[0].start_timestamp - 0.5);
			video.play();

			dispatchAnalyticsTrack("Clip transcript segment click", {
				fingerprint: analyticsFingerprint,
				clip_id: clip.id, // eslint-disable-line camelcase
			});
		},
		[clip, videoRef, analyticsFingerprint],
	);

	const [duration, setDuration] = React.useState<number | null>(null);

	const onVideoLoadedMetadata = React.useCallback(() => {
		if (!videoRef.current) return;
		if (isNaN(videoRef.current.duration)) return;

		setDuration(videoRef.current.duration);
	}, [setDuration, videoRef]);

	const narrowViewport = useIsNarrowViewport();

	const mobileTabOptions = React.useMemo(() => [{ value: "Transcript" }, { value: "Summary" }] as const, []);
	const [mobileActiveTab, setMobileActiveTab] = React.useState<(typeof mobileTabOptions)[number]["value"]>("Summary");

	const [viewCounted, setViewCounted] = React.useState(false);
	const onPlayVideo = React.useCallback(() => {
		if (viewCounted) return;

		setViewCounted(true);

		fetch("/public/meeting_recordings/view/clip/", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"x-csrftoken": window.CSRF_TOKEN,
			},
			body: JSON.stringify({
				clipId: clip.id,
			}),
		});
	}, [viewCounted, clip.id]);

	const [videoCollapsed, setVideoCollapsed] = React.useState(false);
	const toggleVideoCollapsed = React.useCallback(() => setVideoCollapsed(!videoCollapsed), [videoCollapsed]);

	const transcriptEl = (
		<Transcript
			ref={transcriptRef}
			note={null}
			meetingRecording={clip}
			viewer={null}
			transcriptDeletionReason={null}
			transcript={clip.transcript}
			onSegmentClick={onTranscriptSegmentClick}
			videoDuration={duration}
			closeModal={noop}
		/>
	);

	const summaryEl = (
		<div className={styles.publicClipSummaryContainer}>
			<h2 className={styles.publicClipSummaryHeading}>
				Summary
				<small className={styles.publicClipSummaryDuration}>{formatDuration(clip.clipEnd - clip.clipStart)}</small>
			</h2>
			<SummaryText className={styles.publicClipSummaryText}>
				{
					// FIXME: generating state
					clip.summary ?? ""
				}
			</SummaryText>
		</div>
	);

	const videoEl = (
		<TranscriptVideo
			key="video-el"
			ref={videoRef}
			meetingRecording={clip}
			videoPlaybackSettings={videoPlaybackSettings}
			initialVideoTimestamp={initialVideoTimestamp}
			isMiniPlayerActive={false}
			toggleMiniPlayer={noop}
			thumbnailTrack={null}
			isClip
			onTimeUpdate={onVideoTimeUpdate}
			onLoadedMetadata={onVideoLoadedMetadata}
			onPlay={onPlayVideo}
			inClippingMode={false}
			allowRecapDownload={clip?.ownerAccount.allowRecapDownload}
			isCollapsed={videoCollapsed}
			setIsCollapsed={setVideoCollapsed}
		/>
	);

	let content = (
		<div key="preview" className={styles.preview}>
			{!narrowViewport ? (
				<div key="left-panel" className={styles.leftPanel}>
					{transcriptEl}
				</div>
			) : undefined}
			<div key="metadata" className={styles.metadata}>
				<div>{videoEl}</div>
				<CollapseButton isCollapsed={videoCollapsed} onClick={toggleVideoCollapsed} mediaType={clip.mediaType} />
				{narrowViewport ? (
					<>
						<ToggleGroup
							key="toggle-group"
							className={styles.narrowWidthToggleGroup}
							options={mobileTabOptions}
							value={mobileActiveTab}
							onValueChange={setMobileActiveTab}
						/>
						{mobileActiveTab === "Transcript" && transcriptEl}
						{mobileActiveTab === "Summary" && summaryEl}
					</>
				) : (
					summaryEl
				)}
			</div>
		</div>
	);

	useSessionManager(videoRef.current, null, clip.id);

	return (
		<Modal
			onClose={noop}
			className={cx(styles.modal, styles.publicModal)}
			overlayClassName={styles.overlayNoPadding}
			width="100vw"
			height="100vh"
		>
			<Modal.Header className={styles.modalHeader}>
				<div className={styles.modalTitle}>
					{!narrowViewport ? (
						<div className={styles.publicModalHeaderLeft}>
							{clip.ownerAccount && (
								<Avatar
									className={styles.ownerAccountAvatar}
									width="36px"
									user={{
										id: "",
										image: clip.ownerAccount.logo,
										fullName: clip.ownerAccount.name,
									}}
								/>
							)}
							<div>
								<Modal.Title>{clip.title}</Modal.Title>
								{clip.ownerAccount && (
									<div className={styles.publicShareInfoContainer}>Shared by {clip.ownerAccount.name}</div>
								)}
							</div>
						</div>
					) : (
						<Modal.Title>{clip.title}</Modal.Title>
					)}
				</div>
				<Modal.HeaderActions className={styles.modalHeaderActions}>
					<IconButton icon={justCopied ? faCheck : faLink} onClick={copyLink} />
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body noPadding>{content}</Modal.Body>
		</Modal>
	);
}

export function PublicClipModalRenderer({ match, location }: RouteComponentProps<{ clipId: string }>) {
	const { clipId } = match.params;

	// NOTE: This query doesn't run in the GraphQL API; see the warning in
	// PublicTranscriptModalRenderer.
	const { node: clip, viewer } = useLazyLoadQuery<TranscriptModalPublicClipQuery>(
		graphql`
			query TranscriptModalPublicClipQuery($clipId: ID!) {
				node(id: $clipId) {
					...TranscriptModal_PublicClipModal_clip
				}

				viewer {
					videoPlaybackSettings {
						...TranscriptModal_PublicClipModal_videoPlaybackSettings
					}
				}
			}
		`,
		{ clipId },
		{ fetchPolicy: "network-only" },
	);

	const initialVideoTimestamp = getTimestampFromLocation(location);

	// FIXME: Not found state
	if (!clip) return null;
	return (
		<PublicContextProvider type="clip" clipId={clipId}>
			<PublicClipModal
				videoPlaybackSettings={viewer.videoPlaybackSettings}
				clip={clip}
				initialVideoTimestamp={initialVideoTimestamp}
			/>
		</PublicContextProvider>
	);
}
